import { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { Dialog, DialogHeader } from '@astryxdesign/core/Dialog';
import { Layout, LayoutContent, LayoutFooter } from '@astryxdesign/core/Layout';
import { Banner } from '@astryxdesign/core/Banner';
import { Button } from '@astryxdesign/core/Button';
import { HStack } from '@astryxdesign/core/HStack';
import { Step, Stepper } from '@astryxdesign/core/Stepper';
import { Text } from '@astryxdesign/core/Text';
import { VStack } from '@astryxdesign/core/VStack';
import { RowsSkeleton } from '../../widgets/skeletons';
import { useNotificationTemplates, useTelegramActions, useTelegramStatus } from '../../api/telegram';
import { EventPicker, templateChoices } from './EventPicker';
import type { EventChoice } from './EventPicker';
import { ConnectBot } from './ConnectBot';
import { withRegions } from './regions';

type WizardStep = 'events' | 'connect' | 'done';

export function TelegramWizard({ start, onClose }: { start: 'events' | 'connect'; onClose: () => void }) {
  const intl = useIntl();
  const status = useTelegramStatus();
  const templates = useNotificationTemplates();
  const actions = useTelegramActions();
  const [step, setStep] = useState<WizardStep>(start);
  const [choices, setChoices] = useState<EventChoice[] | null>(null);
  const [regions, setRegions] = useState<string[]>([]);
  const [created, setCreated] = useState<string[]>([]);
  const linked = Boolean(status.data?.link && !status.data.link.blocked);

  useEffect(() => {
    if (!choices && templates.data)
      setChoices(templateChoices(templates.data.templates, templates.data.team, intl));
  }, [choices, templates.data, intl]);

  const selected = (choices ?? []).filter((c) => c.selected);
  const save = () => {
    actions.create.mutate(
      selected.map((c) => ({
        name: c.draft.name,
        template: c.draft.template,
        definition: regions.length ? withRegions(c.draft.definition, regions) : c.draft.definition,
        enabled: true,
        cooldownMinutes: c.draft.cooldownMinutes,
        notifyRecovery: c.draft.notifyRecovery,
      })),
      {
        onSuccess: (res) => {
          setCreated(res.items.map((r) => r.name));
          setStep(linked ? 'done' : 'connect');
        },
      },
    );
  };
  const refresh = useRef(actions.refresh);
  useEffect(() => {
    refresh.current = actions.refresh;
  });
  const onLinked = useCallback(() => {
    void refresh.current();
    setStep('done');
  }, []);

  const index = step === 'events' ? 0 : step === 'connect' ? 1 : 2;
  const close = (open: boolean) => {
    if (!open) onClose();
  };
  const footer =
    step === 'events' ? (
      <HStack gap={2} hAlign="end">
        <Button label={intl.formatMessage({ id: 'common.cancel' })} variant="ghost" onClick={onClose} />
        <Button
          label={intl.formatMessage({ id: linked ? 'notifications.save' : 'notifications.next' })}
          variant="primary"
          isDisabled={!selected.length}
          isLoading={actions.create.isPending}
          onClick={save}
        />
      </HStack>
    ) : step === 'connect' ? (
      <HStack gap={2} hAlign="end">
        <Button label={intl.formatMessage({ id: 'notifications.later' })} variant="ghost" onClick={onClose} />
      </HStack>
    ) : (
      <HStack gap={2} hAlign="end">
        <Button
          label={intl.formatMessage({ id: 'notifications.finish' })}
          variant="primary"
          onClick={onClose}
        />
      </HStack>
    );

  return (
    <Dialog isOpen onOpenChange={close} width={720}>
      <Layout
        header={
          <DialogHeader
            title={intl.formatMessage({ id: 'notifications.wizardTitle' })}
            onOpenChange={close}
          />
        }
        content={
          <LayoutContent>
            <VStack gap={4}>
              <Stepper activeStep={index} label={intl.formatMessage({ id: 'notifications.wizardTitle' })}>
                <Step step={0} label={intl.formatMessage({ id: 'notifications.step.events' })} />
                <Step step={1} label={intl.formatMessage({ id: 'notifications.step.connect' })} />
                <Step step={2} label={intl.formatMessage({ id: 'notifications.step.done' })} />
              </Stepper>
              {actions.create.isError ? (
                <Banner
                  status="error"
                  title={intl.formatMessage({ id: 'common.saveFailed' })}
                  collapsible={false}
                />
              ) : null}
              {step === 'events' ? (
                choices ? (
                  <VStack gap={3}>
                    <Text color="secondary">{intl.formatMessage({ id: 'notifications.eventsIntro' })}</Text>
                    <EventPicker
                      choices={choices}
                      onChange={setChoices}
                      regions={regions}
                      onRegionsChange={setRegions}
                    />
                  </VStack>
                ) : (
                  <RowsSkeleton rows={5} />
                )
              ) : null}
              {step === 'connect' ? <ConnectBot onLinked={onLinked} /> : null}
              {step === 'done' ? (
                <div data-qs-telegram-done="">
                  <VStack gap={2}>
                    <Text weight="semibold">{intl.formatMessage({ id: 'notifications.doneTitle' })}</Text>
                    <Text color="secondary">{intl.formatMessage({ id: 'notifications.doneText' })}</Text>
                    {created.length ? (
                      <Text size="sm" color="secondary">
                        {intl.formatMessage({ id: 'notifications.doneList' }, { list: created.join(', ') })}
                      </Text>
                    ) : null}
                  </VStack>
                </div>
              ) : null}
            </VStack>
          </LayoutContent>
        }
        footer={<LayoutFooter>{footer}</LayoutFooter>}
      />
    </Dialog>
  );
}
